import L from 'leaflet';
import mercator from 'projections/mercator';

/**
 * Returns the boundaries for a given set of coordinates
 * @param  {Array} coordinates the object boundaries
 * @return {Array<Array.<Number>>}      the coordinates [SW, NE]
 */
export function getBounds(coordinates) {
  const bounds = new L.Polygon(coordinates).getBounds();
  return [
    [bounds.getSouthWest().lat, bounds.getSouthWest().lng],
    [bounds.getNorthEast().lat, bounds.getNorthEast().lng],
  ];
}

/**
 * Returns the same multidimensional Array with the lat and lng inverted
 * (this is due to Homestead's format)
 * @param  {Array} coordinates the coordinates
 * @return {Array}
 */
export function inverseCoordinates(coordinates) {
  return Array.isArray(coordinates[0])
    ? coordinates.map((coordinate) => {
        if (Array.isArray(coordinate)) {
          if (Array.isArray(coordinate[0])) {
            return inverseCoordinates(coordinate);
          }
          return [...coordinate].reverse();
        }
        return [];
      })
    : [...coordinates].reverse();
}

const projectPointCoordinates = (coord) => {
  const proyectedCoord = mercator({ lon: coord[0], lat: coord[1] });
  return [proyectedCoord.x, proyectedCoord.y];
};

const projectPolygonCoordinates = (coord) => [
  coord[0].map(projectPointCoordinates),
];

const projectMultiPolygonCoordinates = (coord) =>
  coord.map(projectPolygonCoordinates);

/**
 * Apply the geo mercator projection to a geoJson geometry array
 * @param  {Array} geometry GeoJson Geometry
 * @return {Array} new projected coordinates
 */
export const geoMercatorProjection = (geometry) => {
  const { type, coordinates: coord } = geometry;
  let coordinates = [];
  switch (type) {
    case 'MultiPolygon':
      coordinates = projectMultiPolygonCoordinates(coord);
      break;
    case 'Polygon':
      coordinates = projectPolygonCoordinates(coord);
      break;
    case 'Point':
      coordinates = projectPointCoordinates(coord);
      break;
    default:
      return [];
  }
  return { ...geometry, coordinates };
};

/**
 * Project coordinates of a geoJson object
 * @param  {Object} geoJson
 * @return {Object} new geoJson
 */
export const projectCoordinates = (geoJson) => ({
  ...geoJson,
  features: geoJson.features.map((feature) => ({
    ...feature,
    geometry: geoMercatorProjection(feature.geometry),
  })),
});

/**
 * Return a geoJson object based on a geometry objects array
 * @param  {Array} geometries
 * @return {Object} geoJson
 */
export const getGeoJson = (geometries) => ({
  type: 'FeatureCollection',
  features: geometries.map((geometry) => ({
    type: 'Feature',
    properties: {},
    geometry,
  })),
});
